import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import firebase from './firebase';
import { SCREENS, trackScreen } from './analytics';
import { songAudioUrl } from './songs';
import { alacPlayableUrl } from './alac';
import { getSession } from './auth';
import { hasAlbum, loginToSave, saveAlbumFor } from './savedAlbums';
import { PlayIcon, PauseIcon, PrevIcon, NextIcon, LoopIcon } from './icons';

// Public, login-free view of one album (/a/:albumId) or one song (/s/:albumId/:songId).
// It only reads the album and the songs being shared.

const INVALID_KEY = /[.#$[\]/]/;

const read = (path) => firebase.database().ref(path).once('value').then((snap) => snap.val());

// Resolves with the album and its songs in album order (just `onlySongId` when given),
// or null if the link points at something that doesn't exist.
const loadShared = async (albumId, onlySongId) => {
  if (INVALID_KEY.test(albumId) || (onlySongId && INVALID_KEY.test(onlySongId))) return null;
  const album = await read(`albums/${albumId}`);
  if (!album) return null;

  const entries = Object.entries(album.songs || {})
    .filter(([id]) => !onlySongId || id === onlySongId)
    .sort((a, b) => a[1] - b[1]);
  if (onlySongId && entries.length === 0) return null;

  const songs = (await Promise.all(entries.map(async ([id]) => {
    const song = await read(`songs/${id}`);
    return song && { id, name: song.name || "", url: songAudioUrl(song), artistKeys: Object.keys(song.artists || {}) };
  }))).filter(Boolean);
  if (onlySongId && songs.length === 0) return null;

  // The artists, as in the app: the primary owner (under their account name), then the other owners,
  // the collaborators and any artists who aren't on Stew. The ones the owner hid are left off the album
  // line. Artists on Stew link to their profile. A song that credits its own artists shows those instead.
  const owner = album.userId || '';
  const hidden = album.hiddenArtists || {};
  const owners = Object.keys(album.owners || {});
  const others = [...owners, ...Object.keys(album.collaborators || {})]
    .filter((u, i, all) => u !== owner && !INVALID_KEY.test(u) && all.indexOf(u) === i);
  const usernames = [...(owner && !INVALID_KEY.test(owner) ? [owner] : []), ...others];
  // Bands credited on the album; one that was deleted is left out.
  const bandIds = Object.keys(album.bands || {}).filter((id) => !INVALID_KEY.test(id));
  const bandNames = await Promise.all(bandIds.map((id) => read(`bands/${id}/name`).catch(() => null)));
  const bands = bandIds.map((id, n) => ({ key: id, username: '', bandId: id, name: bandNames[n] })).filter((b) => b.name);
  const names = await Promise.all(usernames.map((u) => read(`users/${u}/name`).catch(() => null)));
  const nameOf = (u) => names[usernames.indexOf(u)] || u;
  const isOwner = (u) => owners.includes(u);
  const everyone = [
    // The lead artist is the primary owner under their account name (the album's own `artist` text
    // is only a fallback for an album with no owner).
    ...(owner || album.artist ? [{ key: owner || 'owner', username: owner, name: owner ? nameOf(owner) : album.artist }] : []),
    ...others.map((username) => ({ key: username, username, name: nameOf(username) }))
      .sort((a, b) => Number(isOwner(b.username)) - Number(isOwner(a.username))),
    ...bands,
    ...Object.entries(album.externalArtists || {}).filter(([, v]) => v && v.name).map(([key, v]) => ({ key, username: '', name: v.name })),
  ];
  const albumArtists = everyone.filter((a) => !hidden[a.key]);
  const artistsFor = (keys) => {
    const picked = keys && keys.length ? everyone.filter((a) => keys.includes(a.key)) : [];
    return picked.length ? picked : albumArtists;
  };
  songs.forEach((song) => { song.artists = artistsFor(song.artistKeys); });
  const artists = albumArtists;
  const artist = artists.map((a) => a.name).join(', ');
  return { name: album.name || "", artist, artists, cover: album.albumCoverURL || "", songs };
};

// Artist names, those on Stew linking to their profile. Safe to put inside something clickable.
const ArtistLinks = ({ artists }) => artists.map((a, n) => (
  <React.Fragment key={`${a.key}:${a.name}`}>
    {n > 0 && ', '}
    {a.bandId
      ? <Link to={`/b/${encodeURIComponent(a.bandId)}`} className="artist-link" onClick={(e) => e.stopPropagation()}>{a.name}</Link>
      : a.username
        ? <Link to={`/u/${encodeURIComponent(a.username)}`} className="artist-link" onClick={(e) => e.stopPropagation()}>{a.name}</Link>
        : a.name}
  </React.Fragment>
));

const Cover = ({ src, size }) => {
  const [failed, setFailed] = useState(false);
  const box = { width: size, height: size, borderRadius: 8, display: 'block', margin: '0 auto 24px' };
  return src && !failed
    ? <img src={src} alt="" style={{ ...box, objectFit: 'cover' }} onError={() => setFailed(true)} />
    : <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size / 3, background: 'linear-gradient(135deg, #1db954, #0d6efd)' }}>🍲</div>;
};

const Message = ({ children }) => (
  <div className="text-center text-muted" style={{ width: '90%', margin: '0 auto' }}>{children}</div>
);

const formatTime = (t) => {
  if (!isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const sec = Math.floor(t % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};

// Same rule as the app and the iOS app: a listen is one start that has played for 5 seconds.
const countListen = (songId) => {
  firebase.database().ref(`songs/${songId}/listens`)
    .transaction((n) => (Number(n) || 0) + 1)
    .catch((e) => console.error('Could not count listen', e));
};

// "Save album": attaches the album to the person's account. Someone who isn't logged in goes through login
// (or sign up) and comes back to the album in the app, where it is saved for them.
const SaveAlbum = ({ albumId }) => {
  const session = getSession();
  const [state, setState] = useState(session ? 'checking' : 'ready'); // checking | ready | saving | saved | error
  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    hasAlbum(session.username, albumId)
      .then((has) => !cancelled && setState(has ? 'saved' : 'ready'))
      .catch(() => !cancelled && setState('ready'));
    return () => { cancelled = true; };
  }, [albumId]); // eslint-disable-line react-hooks/exhaustive-deps
  const wrap = { textAlign: 'center', margin: '16px 0 0' };
  if (!session) {
    return <div style={wrap}><Link to={loginToSave(albumId)} className="btn btn-primary">Save to my albums</Link></div>;
  }
  const save = async () => {
    setState('saving');
    try { await saveAlbumFor(session.username, albumId); setState('saved'); } catch (e) { setState('error'); }
  };
  if (state === 'saved') {
    return <div style={wrap}><small className="text-muted">Saved to your albums. <Link to={`/a/${encodeURIComponent(albumId)}`}>Open in Stew</Link></small></div>;
  }
  return (
    <div style={wrap}>
      <Button onClick={save} disabled={state === 'checking' || state === 'saving'}>
        {state === 'saving' ? 'Saving…' : 'Save to my albums'}
      </Button>
      {state === 'error' && <div><small className="text-danger">Couldn't save. Try again.</small></div>}
    </div>
  );
};

const roundButton = (size, active) => ({
  width: size, height: size, borderRadius: '50%', border: 'none', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  fontSize: size / 2, color: active ? '#1db954' : 'inherit', background: 'none',
});

const Share = ({ match }) => {
  const { albumId, songId } = match.params;
  const [state, setState] = useState({ status: 'loading' });
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  // 'off' plays on to the next song and stops at the end; 'all' wraps to the first; 'one' repeats the song.
  const [loop, setLoop] = useState('off');
  const audioRef = useRef(null);
  const pendingRef = useRef(null); // song started but not yet counted as a listen
  const wantPlayRef = useRef(false); // someone pressed play, so a file the browser rejects is worth decoding
  const alacBusyRef = useRef(null); // the file currently being decoded

  useEffect(() => { trackScreen(SCREENS.shared); }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    setCurrent(null);
    setPlaying(false);
    setProgress(0);
    setDuration(0);
    pendingRef.current = null;
    loadShared(albumId, songId)
      .then((data) => {
        if (cancelled) return;
        setState(data ? { status: 'ready', data } : { status: 'missing' });
        if (data && songId) { // a single-song link has its one song ready to play
          setCurrent(0);
          pendingRef.current = data.songs[0].id;
        }
      })
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => { cancelled = true; };
  }, [albumId, songId]);

  const songs = useMemo(() => (state.status === 'ready' ? state.data.songs : []), [state]);

  // The source is set inside the click handler, because Safari only allows playback that starts from one.
  // Chrome and Firefox can't decode Apple Lossless .m4a uploads, so after a play attempt fails, decode the file
  // in JavaScript (src/alac.js) and play a WAV copy. Not tried for the preload of a song nobody pressed play on.
  const alacFallback = () => {
    const audio = audioRef.current;
    const src = audio && audio.getAttribute('src');
    if (!src || !audio.error || src.startsWith('blob:') || alacBusyRef.current === src) return;
    alacBusyRef.current = src;
    const stale = () => audio.getAttribute('src') !== src;
    alacPlayableUrl(src, stale).then((url) => {
      if (!url || stale()) return;
      audio.src = url;
      audio.play().catch(() => {});
    }).catch(() => {}).then(() => { if (alacBusyRef.current === src) alacBusyRef.current = null; });
  };

  const play = (i, restart) => {
    const audio = audioRef.current;
    if (!audio || !songs[i] || !songs[i].url) return;
    wantPlayRef.current = true;
    if (i === current && !restart) {
      if (audio.ended) {
        pendingRef.current = songs[i].id; // pressing play after it finished is a new listen
        audio.currentTime = 0;
      }
      if (audio.paused) audio.play().catch(alacFallback); else audio.pause();
      return;
    }
    if (audio.getAttribute('src') !== songs[i].url) audio.src = songs[i].url;
    else audio.currentTime = 0;
    pendingRef.current = songs[i].id;
    audio.loop = loop === 'one';
    audio.play().catch(alacFallback);
    setCurrent(i);
    setProgress(0);
  };

  const playPrev = () => {
    const audio = audioRef.current;
    // Like most players: within the first seconds go to the previous song, otherwise restart this one.
    if (audio && audio.currentTime > 3) { audio.currentTime = 0; return; }
    if (current > 0) {
      for (let j = current - 1; j >= 0; j--) if (songs[j].url) { play(j); return; }
    }
    if (audio) audio.currentTime = 0;
  };

  const playNext = () => {
    if (songs.length === 0) return;
    for (let n = 1; n <= songs.length; n++) {
      let j = current + n;
      if (j >= songs.length) {
        if (loop !== 'all') break;
        j %= songs.length;
      }
      if (songs[j].url) { play(j, j === current); return; }
    }
    setPlaying(false);
  };

  const cycleLoop = () => {
    const order = songId ? ['off', 'one'] : ['off', 'all', 'one'];
    const next = order[(order.indexOf(loop) + 1) % order.length];
    setLoop(next);
    if (audioRef.current) audioRef.current.loop = next === 'one';
  };

  const onEnded = () => {
    if (current !== null && songs[current]) pendingRef.current = songs[current].id;
    if (songId) { setPlaying(false); return; }
    playNext();
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    setProgress(audio.currentTime);
    setDuration(audio.duration || 0);
    const song = current !== null ? songs[current] : null;
    if (song && pendingRef.current === song.id && !audio.paused && audio.currentTime >= 5) {
      pendingRef.current = null;
      countListen(song.id);
    }
  };

  const seek = (e) => {
    const t = parseFloat(e.target.value);
    audioRef.current.currentTime = t;
    setProgress(t);
  };

  // Space plays/pauses and the arrows seek, unless a control already has the keys.
  const keyHandler = useRef(null);
  keyHandler.current = (e) => {
    if (current === null || e.metaKey || e.ctrlKey || e.altKey) return;
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'button' || tag === 'textarea' || tag === 'select') return;
    const audio = audioRef.current;
    if (e.code === 'Space') { e.preventDefault(); play(current); }
    else if (e.code === 'ArrowRight' && audio) audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + 10);
    else if (e.code === 'ArrowLeft' && audio) audio.currentTime = Math.max(0, audio.currentTime - 10);
  };
  useEffect(() => {
    const onKey = (e) => keyHandler.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock screen / headphone controls.
  const mediaHandlers = useRef({});
  mediaHandlers.current = { play: () => play(current), pause: () => play(current), prev: playPrev, next: playNext };
  useEffect(() => {
    if (!('mediaSession' in navigator)) return undefined;
    const ms = navigator.mediaSession;
    const set = (action, fn) => { try { ms.setActionHandler(action, fn); } catch (e) { /* unsupported action */ } };
    set('play', () => mediaHandlers.current.play());
    set('pause', () => mediaHandlers.current.pause());
    set('previoustrack', () => mediaHandlers.current.prev());
    set('nexttrack', () => mediaHandlers.current.next());
    return () => ['play', 'pause', 'previoustrack', 'nexttrack'].forEach((a) => set(a, null));
  }, []);
  useEffect(() => {
    if (!('mediaSession' in navigator) || state.status !== 'ready' || current === null || !songs[current]) return;
    const { name, cover } = state.data;
    const artist = songs[current].artists.map((a) => a.name).join(', ');
    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: songs[current].name, artist, album: name, artwork: cover ? [{ src: cover }] : [],
      });
    } catch (e) { /* not supported */ }
  }, [current, state, songs]);

  if (state.status === 'loading') return <div className="text-center"><Spinner animation="border" /></div>;
  if (state.status === 'missing') return <Message><h4>Link not found</h4>This song or album may have been removed.</Message>;
  if (state.status === 'error') return <Message><h4>Couldn't load this link</h4>Check your connection and try again.</Message>;

  const { name, cover } = state.data;
  // On a single-song link the song's own artists apply; otherwise the album's.
  const shownArtists = songId && songs[0] ? songs[0].artists : state.data.artists;
  const artist = shownArtists.map((a) => a.name).join(', ');
  const artistLinks = <ArtistLinks artists={shownArtists} />;
  const hasSong = current !== null && !!songs[current];
  const canPlay = hasSong && !!songs[current].url;

  const audio = (
    <audio ref={audioRef} preload="metadata" src={songId && songs[0] && songs[0].url ? songs[0].url : undefined}
      onEnded={onEnded} onTimeUpdate={onTimeUpdate} onLoadedMetadata={onTimeUpdate}
      onError={() => { if (wantPlayRef.current) alacFallback(); }}
      onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
  );

  const controls = (big) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, minWidth: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatTime(progress)}</span>
        <input type="range" aria-label="Seek" min="0" max={duration || 0} step="0.1" value={Math.min(progress, duration || 0)}
          onChange={seek} disabled={!canPlay} style={{ flex: 1, minWidth: 0, accentColor: '#1db954' }} />
        <span style={{ fontSize: 12, minWidth: 36, fontVariantNumeric: 'tabular-nums' }}>{formatTime(duration)}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginTop: 8 }}>
        <div>
          <button type="button" onClick={cycleLoop} style={roundButton(big ? 48 : 40, loop !== 'off')}
            aria-label={loop === 'off' ? 'Repeat off' : loop === 'one' ? 'Repeating this song' : 'Repeating the album'}
            aria-pressed={loop !== 'off'}
            title={loop === 'off' ? 'Repeat: off' : loop === 'one' ? 'Repeat: this song' : 'Repeat: whole album'}>
            <LoopIcon one={loop === 'one'} />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: big ? 16 : 8 }}>
          {!songId && (
            <button type="button" onClick={playPrev} disabled={!hasSong} style={roundButton(big ? 48 : 40)} aria-label="Previous song" title="Previous">
              <PrevIcon />
            </button>
          )}
          <button type="button" onClick={() => play(hasSong ? current : songs.findIndex(x => x.url))}
            disabled={!songs.some(x => x.url)}
            style={{ ...roundButton(big ? 64 : 48), background: '#1db954', color: '#fff', fontSize: big ? 28 : 22 }}
            aria-label={playing ? 'Pause' : 'Play'} title={playing ? 'Pause' : 'Play'}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
          {!songId && (
            <button type="button" onClick={playNext} disabled={!hasSong} style={roundButton(big ? 48 : 40)} aria-label="Next song" title="Next">
              <NextIcon />
            </button>
          )}
        </div>
        <div />
      </div>
    </div>
  );

  const footer = (
    <small className="text-muted text-center" style={{ display: 'block', margin: '32px 0' }}>
      Shared from <Link to="/login">🍲 Stew</Link>
    </small>
  );

  if (songId) {
    const song = songs[0];
    return (
      <div style={{ maxWidth: 480, width: '90%', margin: '0 auto', paddingTop: 48 }}>
        <Helmet title={`${song.name} · ${artist || name}`} />
        <Cover src={cover} size={220} />
        <h2 className="text-center" style={{ margin: '8px 0' }}>{song.name}</h2>
        <h5 className="text-center text-muted">
          {artistLinks}{shownArtists.length > 0 && name ? ' · ' : ''}{name}
        </h5>
        <SaveAlbum albumId={albumId} />
        {audio}
        {song.url ? (
          <div style={{ marginTop: 24 }}>{controls(true)}</div>
        ) : (
          <p className="text-center text-muted mt-4">No audio is available for this song.</p>
        )}
        {footer}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700, width: '90%', margin: '0 auto', paddingTop: 48, paddingBottom: 160 }}>
      <Helmet title={`${name} · ${artist}`} />
      <Cover src={cover} size={180} />
      <h2 className="text-center" style={{ margin: '8px 0' }}>{name}</h2>
      <h4 className="text-center" style={{ color: 'gray' }}>{artistLinks}</h4>
      <SaveAlbum albumId={albumId} />
      <ul style={{ listStyle: 'none', padding: 0, margin: '32px 0' }}>
        {songs.map((song, i) => (
          <li key={song.id}
            onClick={() => play(i)}
            style={{
              padding: '16px 24px', margin: '8px 0', borderRadius: 6,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              cursor: song.url ? 'pointer' : 'default', opacity: song.url ? 1 : 0.5,
              backgroundColor: i === current ? 'var(--song-current-bg)' : 'var(--song-bg)',
            }}>
            <span style={{ minWidth: 0 }}>
              {song.name}
              {song.artistKeys.length > 0 && (
                <small className="text-muted" style={{ display: 'block' }}><ArtistLinks artists={song.artists} /></small>
              )}
            </span>
            <span aria-hidden="true">{i === current && playing ? '⏸' : '▶'}</span>
          </li>
        ))}
      </ul>
      {songs.length === 0 && <p className="text-center text-muted">This album has no songs yet.</p>}
      {footer}
      {audio}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, padding: '8px 5%',
        background: '#222', color: 'white', display: current === null ? 'none' : 'block',
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ fontWeight: 'bold', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {hasSong && songs[current].name}
          </div>
          {hasSong && songs[current].artists.length > 0 && (
            <div style={{ textAlign: 'center', fontSize: 13, opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {songs[current].artists.map((a) => a.name).join(', ')}
            </div>
          )}
          {controls(false)}
        </div>
      </div>
    </div>
  );
};

export default Share;
